import { parseDate } from '@eventbrite/datetime';
import { Card } from '@eventbrite/eds-card';
import { MediaCardContent } from '@eventbrite/eds-media-card-content';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React from 'react';
import {
    GA_CITY_BROWSE_CATEGORY,
    GA_VIEW_ARTICLE_ACTION,
} from '../../constants/analytics';
import './articleCard.scss';

const LIST_TYPE = 'list';

const _formatByLine = (byline: string) => (
    <span className="eds-text-weight--heavy">{byline}</span>
);

const _formatDate = (rawDate: {
    date: string;
    dayFormat?: string;
    monthFormat?: string;
    yearFormat?: string;
}) => {
    const { day, month } = parseDate(rawDate);

    return gettext('%(month)s %(day)s', { month, day });
};

export const AuthorAndDate = ({
    author,
    date,
}: {
    author: string | JSX.Element;
    date: string | JSX.Element | GenericLazyString;
}) => (
    <span>
        {author} • {date}
    </span>
);

const SubContentOne = ({
    author,
    date,
    summary,
}: {
    author: string | JSX.Element;
    date: string | JSX.Element | GenericLazyString;
    summary?: string;
}) => {
    let component = <AuthorAndDate author={author} date={date} />;

    if (summary) {
        component = <span>{summary}</span>;
    }

    return component;
};

const SubContentTwo = ({
    author,
    date,
    summary,
}: {
    author: string | JSX.Element;
    date: string | JSX.Element | GenericLazyString;
    summary?: string;
}) => {
    let component = null;

    if (summary) {
        component = (
            <div className="eds-l-pad-top-3">
                <AuthorAndDate author={author} date={date} />
            </div>
        );
    }

    return component;
};

interface ArticleCardProps {
    name?: string;
    url?: string;
    id?: string;
    imageUrl?: string;
    edgeColor?: string;
    byline?: string;
    published?: {
        date: string;
        dayFormat?: string;
        monthFormat?: string;
        yearFormat?: string;
    };
    summary?: string;
    onClick?: Function;
    trackGAEventOnPageUnload?: Function;
    style?: 'standard' | 'mini';
    imageStyle?:
        | 'fixed'
        | 'fluid'
        | 'rounded'
        | 'square'
        | 'squashed-landscape';
}

export class ArticleCard extends React.PureComponent<ArticleCardProps> {
    _handleArticleClick = () => {
        const { name, onClick, trackGAEventOnPageUnload } = this.props;

        trackGAEventOnPageUnload?.({
            category: GA_CITY_BROWSE_CATEGORY,
            action: GA_VIEW_ARTICLE_ACTION,
            label: name,
        });

        if (onClick) {
            onClick();
        }
    };

    render() {
        const {
            imageUrl,
            edgeColor,
            name,
            url,
            byline,
            style,
            imageStyle,
            summary,
            published,
        } = this.props;

        const classes = 'eds-l-pad-hor-3 eds-l-pad-vert-2';
        const cardClasses = classNames('eds-g-cell', 'eds-g-cell-1-1');

        let contentOne;

        let contentTwo;

        if (byline && published) {
            const formattedAuthor = _formatByLine(byline);
            const formattedDate = _formatDate(published);

            contentOne = (
                <SubContentOne
                    author={formattedAuthor}
                    date={formattedDate}
                    summary={summary}
                />
            );

            contentTwo = (
                <SubContentTwo
                    author={formattedAuthor}
                    date={formattedDate}
                    summary={summary}
                />
            );
        }

        const truncatedTitle = (
            <div className="card-text--truncated__three">{name}</div>
        );

        const articleImage = imageUrl;

        return (
            <div className={classes}>
                <Card style="neutral">
                    <div className={cardClasses}>
                        <MediaCardContent
                            style={style}
                            type={LIST_TYPE}
                            imageStyle={imageStyle}
                            imageUrl={articleImage}
                            placeholderBgColor={edgeColor}
                            linkUrl={url}
                            onClick={this._handleArticleClick}
                            title={truncatedTitle}
                            shouldShowDivider={true}
                            subContentOne={contentOne}
                            subContentTwo={contentTwo}
                        />
                    </div>
                </Card>
            </div>
        );
    }
}

export default ArticleCard;
